(() => {
  'use strict';

  if (window.__SKW_INTENT_SESSION__) return;
  window.__SKW_INTENT_SESSION__ = true;

  const STORAGE_KEY = 'skw_intent_session_v2';
  const LEGACY_KEY = 'skw_intent_session_v1';
  const FAST_THRESHOLD = 6;
  const BROWSE_THRESHOLD = -4;
  const FRICTION_THRESHOLD = 3;
  const context = window.SKW_CLARITY_CONTEXT || {};
  const bootAt = Date.now();

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const normalize = (value) => String(value || '')
    .toLocaleLowerCase('de-DE')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const uniquePush = (list, value, limit = 16) => {
    if (!value) return Array.isArray(list) ? list : [];
    const next = Array.isArray(list) ? list.filter((item) => item !== value) : [];
    next.push(value);
    return next.slice(-limit);
  };

  const freshState = () => ({
    version: 2,
    score: 0,
    mode: 'select',
    startedAt: bootAt,
    lastAt: 0,
    lastPath: '',
    lastType: '',
    pages: 0,
    collectionViews: [],
    productViews: [],
    productViewCounts: {},
    contentViews: 0,
    buySignals: 0,
    decisionSignals: 0,
    detailSignals: 0,
    filterUses: 0,
    searches: [],
    addToCart: false,
    cartAdds: 0,
    cartRemovals: 0,
    checkoutStarted: false,
    checkoutAt: 0,
    checkoutReturnDetected: false,
    frictionScore: 0,
    timePenaltyApplied: false,
    pageSignals: {},
  });

  const migrateLegacy = () => {
    try {
      const legacy = JSON.parse(window.sessionStorage.getItem(LEGACY_KEY) || 'null');
      if (!legacy || legacy.version !== 1) return null;
      const migrated = {
        ...freshState(),
        score: Number.isFinite(legacy.score) ? legacy.score : 0,
        startedAt: Number(legacy.startedAt) || bootAt,
        lastAt: Number(legacy.lastAt) || 0,
        lastPath: legacy.lastPath || '',
        lastType: legacy.lastType || '',
        pages: Number(legacy.pages) || 0,
        collectionViews: Array.isArray(legacy.collectionViews) ? legacy.collectionViews : [],
        productViews: Array.isArray(legacy.productViews) ? legacy.productViews : [],
        contentViews: Number(legacy.contentViews) || 0,
        buySignals: Number(legacy.buySignals) || 0,
        addToCart: Boolean(legacy.addToCart),
        checkoutStarted: Boolean(legacy.checkoutStarted),
        timePenaltyApplied: Boolean(legacy.timePenaltyApplied),
      };
      window.sessionStorage.removeItem(LEGACY_KEY);
      return migrated;
    } catch (_) {
      return null;
    }
  };

  const readState = () => {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (parsed && parsed.version === 2 && Number.isFinite(parsed.score)) {
        return { ...freshState(), ...parsed };
      }
      return migrateLegacy() || freshState();
    } catch (_) {
      return freshState();
    }
  };

  let state = readState();
  let lastAppliedMode = '';

  const writeState = () => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  };

  const pageType = () => {
    if (context.pageType) return context.pageType;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/search')) return 'search';
    if (path.includes('/cart')) return 'cart';
    if (path.includes('/blogs/')) return path.split('/').filter(Boolean).length >= 4 ? 'article' : 'blog';
    if (path.includes('/pages/')) return 'landing';
    if (path === '/' || /^\/[a-z]{2}\/?$/.test(path)) return 'home';
    return 'other';
  };

  const currentProductKey = () => context.resourceHandle || (pageType() === 'product' ? window.location.pathname : '');
  const searchTerms = () => normalize(new URLSearchParams(window.location.search).get('q'));

  const getMode = () => {
    const highIntent = state.checkoutStarted || state.addToCart || state.buySignals > 0;
    if (state.frictionScore >= FRICTION_THRESHOLD && highIntent) return 'friction';
    if (state.checkoutStarted || state.addToCart) return 'fast';
    if (state.productViews.length >= 3 && state.decisionSignals < 2) return 'compare';
    if (state.score >= FAST_THRESHOLD) return 'fast';
    if (state.score <= BROWSE_THRESHOLD || (state.contentViews >= 2 && state.buySignals === 0 && state.score < 2)) return 'browse';
    return 'select';
  };

  const installStyle = () => {
    if (document.getElementById('skw-intent-session-style')) return;
    const style = document.createElement('style');
    style.id = 'skw-intent-session-style';
    style.textContent = `
      html[data-skw-intent="fast"] .skw-search-intent__topic-grid,
      html[data-skw-intent="fast"] div.page-width[class*="skw-theme-choice-"],
      html[data-skw-intent="fast"] .skw-collection-description-bottom,
      html[data-skw-intent="fast"] product-recommendations,
      html[data-skw-intent="fast"] .related-products,
      html[data-skw-intent="friction"] .skw-collection-description-bottom,
      html[data-skw-intent="friction"] product-recommendations,
      html[data-skw-intent="friction"] .related-products,
      html[data-skw-intent="compare"] div.page-width[class*="skw-theme-choice-"],
      html[data-skw-intent="compare"] .skw-collection-description-bottom {
        display: none !important;
      }
      html[data-skw-intent="fast"] .skw-fast-priority,
      html[data-skw-intent="friction"] .skw-fast-priority {
        order: -10 !important;
      }
    `;
    document.head.appendChild(style);
  };

  const emitMode = (mode, reason = '') => {
    if (typeof window.clarity === 'function') {
      try {
        window.clarity('set', 'skw_intent_mode', mode);
        window.clarity('set', 'skw_intent_score', String(state.score));
        window.clarity('set', 'skw_intent_friction', String(state.frictionScore));
        if (reason) window.clarity('set', 'skw_intent_reason', String(reason).slice(0, 80));
        if (mode !== lastAppliedMode) window.clarity('event', 'skw_intent_change');
      } catch (_) {}
    }

    if (mode !== lastAppliedMode) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'skw_intent_change',
        skw_intent_mode: mode,
        skw_intent_score: state.score,
        skw_intent_friction: state.frictionScore,
      });
    }
  };

  const applyMode = (reason = '') => {
    const next = getMode();
    state.mode = next;
    document.documentElement.dataset.skwIntent = next;
    installStyle();
    writeState();
    emitMode(next, reason);
    const changed = next !== lastAppliedMode;
    lastAppliedMode = next;
    window.dispatchEvent(new CustomEvent('skw:intentchange', {
      detail: { mode: next, score: state.score, frictionScore: state.frictionScore, changed, reason },
    }));
  };

  const addScore = (points, reason, onceKey = '') => {
    if (!Number.isFinite(points) || points === 0) return;
    if (onceKey) {
      state.pageSignals = state.pageSignals || {};
      if (state.pageSignals[onceKey]) return;
      state.pageSignals[onceKey] = true;
    }
    state.score = clamp((Number(state.score) || 0) + points, -16, 24);
    applyMode(reason);
  };

  const addFriction = (points, reason, onceKey = '') => {
    if (!Number.isFinite(points) || points <= 0) return;
    if (onceKey) {
      state.pageSignals = state.pageSignals || {};
      if (state.pageSignals[onceKey]) return;
      state.pageSignals[onceKey] = true;
    }
    state.frictionScore = clamp((Number(state.frictionScore) || 0) + points, 0, 12);
    applyMode(reason);
  };

  const broadTerms = [
    'halloween', 'herbst', 'herfst', 'weihnacht', 'kerst', 'advent', 'ostern', 'pasen', 'fruhling', 'lente',
    'geschenk', 'cadeau', 'gift', 'deko', 'decor', 'dekoration', 'ritual', 'rituale', 'idee', 'ideen',
    'inspiration', 'wohnidee', 'selfcare', 'achtsamkeit', 'zen', 'wellness', 'wohnen', 'interior'
  ];
  const concreteTerms = [
    'duftkerz', 'geurkaars', 'kerze', 'kaars', 'raucherstab', 'wierookstok', 'weihrauch', 'raumduft',
    'raumparfum', 'diffuser', 'kamergeur', 'huisgeur', 'matcha', 'tee', 'thee', 'tierfigur', 'dierenbeeld',
    'totenkopf', 'skull', 'kurbis figur', 'kurbisfigur', 'pompoen figuur', 'kerzenhalter', 'teelichthalter',
    'chakra kerze', 'chakraol', 'palo santo', 'duftol', 'rauchermischung'
  ];

  const includesAny = (text, terms) => terms.some((term) => text.includes(normalize(term)));

  const classifySearch = (query) => {
    if (!query) return 0;
    if (includesAny(query, concreteTerms)) return 4;
    const words = query.split(/\s+/).filter(Boolean);
    if (includesAny(query, broadTerms)) return words.length >= 4 ? 0 : -2;
    if (words.length >= 3) return 2;
    if (words.length === 2) return 1;
    return 0;
  };

  const isInternalReferrer = () => {
    if (!document.referrer) return false;
    try {
      return new URL(document.referrer).origin === window.location.origin;
    } catch (_) {
      return false;
    }
  };

  const detectCheckoutReturn = () => {
    if (!state.checkoutStarted || !state.checkoutAt || state.checkoutReturnDetected) return;
    const elapsed = bootAt - Number(state.checkoutAt);
    if (elapsed < 5000 || elapsed > 30 * 60 * 1000) return;
    state.checkoutReturnDetected = true;
    state.frictionScore = clamp(state.frictionScore + 3, 0, 12);
    state.score = clamp(state.score + 2, -16, 24);
  };

  const recordSearch = () => {
    const query = searchTerms();
    if (!query) return;
    state.searches = uniquePush(state.searches, query, 8);
    addScore(classifySearch(query), 'search_intent');
    if (state.searches.length >= 3 && state.buySignals === 0) {
      addScore(-2, 'multiple_searches_without_decision', `searches:${state.searches.length}`);
    }
  };

  const recordPage = () => {
    const now = Date.now();
    const type = pageType();
    const path = `${window.location.pathname}${window.location.search}`;
    const previousType = state.lastType;
    const previousAt = Number(state.lastAt) || 0;
    const elapsed = previousAt ? now - previousAt : null;
    const sameRapidReload = state.lastPath === path && elapsed !== null && elapsed < 10000;

    if (!sameRapidReload) {
      state.pages += 1;
      state.pageSignals = {};

      if (type === 'product') {
        const productKey = currentProductKey();
        state.productViews = uniquePush(state.productViews, productKey);
        state.productViewCounts[productKey] = (Number(state.productViewCounts[productKey]) || 0) + 1;

        if (state.pages === 1 && !isInternalReferrer()) addScore(4, 'direct_product_entry');
        else addScore(2, 'product_view');

        if (state.productViewCounts[productKey] === 2) addScore(3, 'product_revisit');

        if ((previousType === 'collection' || previousType === 'search') && elapsed !== null && elapsed <= 45000) {
          addScore(3, 'fast_product_selection');
        }

        if (state.productViews.length >= 3 && !state.addToCart && state.decisionSignals < 2) applyMode('product_comparison');
      } else if (type === 'collection') {
        state.collectionViews = uniquePush(state.collectionViews, context.resourceHandle || window.location.pathname);
        if (state.pages === 1 && !isInternalReferrer()) addScore(1, 'direct_collection_entry');
        if (state.collectionViews.length >= 3 && state.buySignals === 0) addScore(-3, 'multi_collection_browse');
      } else if (type === 'search') {
        recordSearch();
      } else if (type === 'article' || type === 'blog') {
        state.contentViews += 1;
        if (state.buySignals === 0) addScore(-2, 'content_browse');
      } else if (type === 'landing') {
        state.contentViews += 1;
        if (state.contentViews >= 2 && state.buySignals === 0) addScore(-1, 'landing_browse');
      } else if (type === 'cart') {
        state.buySignals += 1;
        addScore(5, 'cart_view');
      }
    }

    state.lastAt = now;
    state.lastPath = path;
    state.lastType = type;
    applyMode('page_view');
  };

  const oncePerPage = (key) => `interaction:${window.location.pathname}:${key}`;

  const markAddToCart = () => {
    state.cartAdds += 1;
    if (!state.addToCart) state.buySignals += 1;
    state.addToCart = true;
    addScore(8, 'add_to_cart', oncePerPage('add_to_cart'));
    if (state.cartRemovals > 0 && state.cartAdds > 1) addFriction(1, 'repeated_cart_change');
  };

  const markCheckout = () => {
    if (!state.checkoutStarted) state.buySignals += 1;
    state.checkoutStarted = true;
    state.checkoutAt = Date.now();
    addScore(12, 'checkout_start', oncePerPage('checkout'));
  };

  const markCartRemoval = () => {
    state.cartRemovals += 1;
    state.score = clamp(state.score - 1, -16, 24);
    addFriction(state.cartRemovals >= 2 ? 2 : 1, 'cart_removal');
  };

  const requestRemovesCartItem = (args) => {
    const url = String(args[0]?.url || args[0] || '');
    if (!url.includes('/cart/change') && !url.includes('/cart/update')) return false;
    const options = args[1] || {};
    const body = options.body;
    if (!body) return false;
    try {
      if (typeof body === 'string') {
        const parsed = JSON.parse(body);
        if (Number(parsed.quantity) === 0) return true;
        if (parsed.updates && Object.values(parsed.updates).some((value) => Number(value) === 0)) return true;
      }
      if (body instanceof URLSearchParams || body instanceof FormData) {
        if (Number(body.get('quantity')) === 0) return true;
      }
    } catch (_) {}
    return false;
  };

  const isReviewTarget = (target, text) => {
    const signature = normalize(`${target.id || ''} ${target.className || ''} ${text}`);
    return signature.includes('review') || signature.includes('bewertung') || signature.includes('recensie') || signature.includes('judge me') || signature.includes('air reviews');
  };

  const isShippingTarget = (target, text) => {
    const signature = normalize(`${target.id || ''} ${target.className || ''} ${text}`);
    return ['versand', 'liefer', 'shipping', 'delivery', 'zahlung', 'payment', 'verzending', 'levering', 'betaling'].some((term) => signature.includes(term));
  };

  const isMediaTarget = (target) => Boolean(target.closest(
    '.product__media-toggle, .product__media-item, .thumbnail, .thumbnail-list, .product-media-modal, [data-media-id], button[aria-label*="zoom" i]'
  ));

  const bindInteractions = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = String(form.getAttribute('action') || '').toLowerCase();
      if (action.includes('/cart/add')) markAddToCart();
      if (event.submitter?.name === 'checkout') markCheckout();
    }, true);

    document.addEventListener('click', (event) => {
      const element = event.target instanceof Element ? event.target : null;
      if (!element) return;
      const target = element.closest('a, button, input, summary, [role="button"]');
      if (!target) return;

      const href = String(target.getAttribute('href') || '').toLowerCase();
      const name = String(target.getAttribute('name') || '').toLowerCase();
      const text = normalize(target.textContent || target.getAttribute('value') || target.getAttribute('aria-label'));

      if (href.includes('/checkout') || name === 'checkout' || text.includes('zur kasse') || text.includes('checkout') || text.includes('afrekenen')) {
        markCheckout();
        return;
      }

      if (href.includes('/products/') && target.closest('.card-wrapper, .card, .product-grid')) {
        addScore(2, 'product_card_click', oncePerPage(`product_click:${href}`));
      }

      if (href.includes('filter.') || href.includes('filter%5b') || target.closest('facet-filters-form, .facets-container')) {
        state.filterUses += 1;
        addScore(state.filterUses === 1 ? 1 : 2, 'filter_use', oncePerPage(`filter:${state.filterUses}`));
      }

      if (isMediaTarget(target)) {
        state.detailSignals += 1;
        addScore(1, 'product_media_detail', oncePerPage('media_detail'));
      }

      if (isReviewTarget(target, text)) {
        state.detailSignals += 1;
        addScore(2, 'reviews_opened', oncePerPage('reviews'));
      }

      if (isShippingTarget(target, text)) {
        state.detailSignals += 1;
        addScore(3, 'shipping_or_payment_info', oncePerPage('shipping_payment'));
      }

      const wishlistSignature = normalize(`${target.className || ''} ${text}`);
      if (wishlistSignature.includes('wishlist') || wishlistSignature.includes('wunschliste') || wishlistSignature.includes('favoriet')) {
        addScore(1, 'wishlist', oncePerPage('wishlist'));
      }
    }, true);

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;

      if (target.closest('variant-selects, variant-radios, .product-form__input') && target.name !== 'quantity') {
        state.decisionSignals += 1;
        addScore(3, 'variant_choice', oncePerPage('variant_choice'));
      }

      const fieldName = normalize(target.name);
      if (fieldName.includes('properties') || fieldName.includes('personalisierung') || fieldName.includes('personalisatie') || target.closest('[data-skw-personalization]')) {
        state.decisionSignals += 2;
        addScore(4, 'personalization', oncePerPage('personalization'));
      }

      if (target.name === 'quantity' || target.closest('quantity-input')) {
        const current = Number(target.value);
        const previous = Number(target.dataset.skwPreviousQuantity || 1);
        target.dataset.skwPreviousQuantity = String(current || 1);
        if (Number.isFinite(current) && current > previous && current > 1) {
          state.decisionSignals += 1;
          addScore(4, 'quantity_increase', oncePerPage(`quantity_up:${current}`));
        } else if (Number.isFinite(current) && current < previous && pageType() === 'cart') {
          addFriction(1, 'quantity_decrease', oncePerPage(`quantity_down:${current}`));
        }
      }
    }, true);

    document.querySelectorAll('input[name="quantity"], quantity-input input').forEach((input) => {
      input.dataset.skwPreviousQuantity = input.value || '1';
    });

    const originalFetch = window.fetch;
    if (typeof originalFetch === 'function' && !originalFetch.__skwIntentWrapped) {
      const wrappedFetch = function (...args) {
        const url = String(args[0]?.url || args[0] || '');
        const removesItem = requestRemovesCartItem(args);
        const response = originalFetch.apply(this, args);
        if (url.includes('/cart/add')) markAddToCart();
        if (removesItem) markCartRemoval();
        return response;
      };
      wrappedFetch.__skwIntentWrapped = true;
      window.fetch = wrappedFetch;
    }
  };

  const applyTimeSignal = () => {
    if (state.timePenaltyApplied || state.buySignals > 0) return;
    if (Date.now() - state.startedAt < 180000 || state.pages < 3) return;
    state.timePenaltyApplied = true;
    addScore(-2, 'long_browse_without_purchase_signal');
  };

  detectCheckoutReturn();
  recordPage();
  bindInteractions();

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted || state.checkoutReturnDetected || !state.checkoutStarted || !state.checkoutAt) return;
    const elapsed = Date.now() - Number(state.checkoutAt);
    if (elapsed >= 5000 && elapsed <= 30 * 60 * 1000) {
      state.checkoutReturnDetected = true;
      state.frictionScore = clamp(state.frictionScore + 3, 0, 12);
      state.score = clamp(state.score + 2, -16, 24);
      applyMode('checkout_return');
    }
  });

  window.SKWIntent = {
    getState: () => ({ ...state }),
    getMode: () => state.mode,
    reset: () => {
      state = freshState();
      lastAppliedMode = '';
      applyMode('reset');
    },
  };

  window.setInterval(applyTimeSignal, 60000);
})();
